import { AlertModal } from "@/components/generic/AlertModal";
import { IntegrationSetupWizard } from "@/components/integrations";
import { PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import { connectionInfoAtom } from "@/data/integration";
import {
  getIntegrationConfigurationMetadataQueryOptions,
  getIntegrationConnectionInfoQueryOptions,
  getIntegrationSettingsQueryOptions,
  getProviderCapabilitiesQueryOptions,
  useIntegrationConnectionInfoMutation,
} from "@/queries/integrations";
import { SettingsSection } from "@/types/integrations";
import { queryClient } from "@/utils/api";
import { useIsMutating } from "@tanstack/react-query";
import {
  createFileRoute,
  useBlocker,
  useNavigate,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useAtom } from "jotai";
import { CircleQuestionMarkIcon } from "lucide-react";
import { useMemo, useRef } from "react";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute(
  "/_dashboard/settings/_admin/integrations/$integration/$mode/",
)({
  component: RouteComponent,
  pendingMinMs: 1000,
  params: {
    parse: (params: { integration: string; mode: string }) =>
      z
        .object({
          integration: z.string().toLowerCase(),
          mode: z.enum(["setup", "update"]),
        })
        .parse(params),
  },
  validateSearch: zodValidator(
    z.object({
      step: z
        .enum([
          "authentication",
          "fields",
          "settings",
          "notifications",
          "success",
        ] as const)
        .optional(),
    }),
  ),
  preloadStaleTime: 0,
  loader: async ({ params, context }) => {
    const { integration, mode } = params;
    let settings: SettingsSection[] | undefined = undefined;

    const configMetadata = await context.queryClient.ensureQueryData(
      getIntegrationConfigurationMetadataQueryOptions(integration),
    );
    const connectionInfo = await context.queryClient.ensureQueryData(
      getIntegrationConnectionInfoQueryOptions(integration),
    );

    const capabilities = await context.queryClient.ensureQueryData(
      getProviderCapabilitiesQueryOptions(integration),
    );

    if (mode !== "setup") {
      settings = await context.queryClient.ensureQueryData(
        getIntegrationSettingsQueryOptions(integration),
      );
    }

    return {
      configMetadata,
      connectionInfo,
      capabilities,
      hasSettings: !!settings && settings.length > 0,
    };
  },
});

function RouteComponent() {
  const { hasSettings, capabilities, configMetadata, connectionInfo } =
    Route.useLoaderData();
  const { user } = Route.useRouteContext();
  const { integration, mode } = Route.useParams();
  const { step } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [, setConnectionInfo] = useAtom(connectionInfoAtom);

  const intentionalNavRef = useRef(false);

  const connectMutations = useIsMutating(
    {
      mutationKey: ["connect-integration", mode === "setup" ? "post" : "put"],
    },
    queryClient,
  );

  const { mutateAsync, isPending } = useIntegrationConnectionInfoMutation(
    integration,
    mode === "setup" ? "post" : "put",
  );

  const disableBlock = useMemo(() => {
    return connectMutations !== 0;
  }, [connectMutations]);

  const { status, proceed, reset } = useBlocker({
    disabled: disableBlock,
    shouldBlockFn: () => {
      return true;
    },
    withResolver: true,
  });

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Opsætning" />
      <PageContent className="h-full px-14 pt-7 pb-14">
        <SettingsContainer
          className="flex size-full flex-col"
          backLinkOptions={
            mode === "setup"
              ? { to: "/settings/integrations" }
              : {
                  to: "/settings/integrations/$integration/settings",
                  params: { integration },
                }
          }
        >
          <div className="grow">
            <IntegrationSetupWizard
              user={user}
              integration={integration}
              configMetadata={configMetadata}
              onCancel={() => {
                navigate(
                  mode === "setup"
                    ? {
                        to: "/settings/integrations",
                      }
                    : {
                        to: "/settings/integrations/$integration/settings",
                        params: { integration },
                      },
                );
              }}
              onComplete={async (connectionInfo) => {
                const promise = mutateAsync({ data: connectionInfo });
                toast.promise(promise, {
                  loading:
                    mode === "setup"
                      ? "Opretter integration..."
                      : "Opdaterer integration...",
                  success:
                    mode === "setup"
                      ? "Integration oprettet"
                      : "Integration opdateret",
                  error: `Der skete en fejl under ${mode === "setup" ? "oprettelsen" : "opdateringen"} af integrationen`,
                });
                const updatedConnectionInfo = { ...(await promise) };
                setConnectionInfo(updatedConnectionInfo);
                navigate({
                  to: "/settings/integrations/$integration/settings",
                  params: { integration },
                  ignoreBlocker: true,
                });
              }}
              mode={mode}
              hasSettings={hasSettings}
              capabilities={capabilities}
              connectionInfo={connectionInfo}
              initialStep={step}
              onStepChange={(newStep) =>
                navigate({
                  search: { step: newStep },
                  replace: true,
                  ignoreBlocker: true,
                })
              }
            />
          </div>
        </SettingsContainer>
      </PageContent>
      {status === "blocked" && (
        <AlertModal
          title="Du er ved at forlade opsætningen"
          description="Hvis du forlader opsætningen nu, vil dine ændringer gå tabt. Er du sikker på at du vil fortsætte?"
          onClose={reset}
          onConfirm={() => {
            sessionStorage.removeItem(`integration-${integration}-auth`);
            sessionStorage.removeItem(`integration-${integration}-fields`);
            sessionStorage.removeItem(`integration-${integration}-settings`);
            setConnectionInfo(null);
            proceed?.();
          }}
          icon={<CircleQuestionMarkIcon className="text-theme-active" />}
        />
      )}
    </div>
  );
}
